import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Switch,
  Tooltip,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { RootState } from '../../../store';
import AddEditDataAgentSourceUIForm from './AddEditDataAgentSourceUIForm';
import { useGetDataAgentSourcesQuery, useEnableDisableDataAgentSourcesMutation } from '../../../api/dataAgentApiSlice';
import { showToaster, CustomConfirmDialog } from '../../../utils/globalImports';
import { useTranslation } from 'react-i18next';

interface DataAgentSourceTabProps {
  dataAgentId: string;
}

const DataAgentSourceTab: React.FC<DataAgentSourceTabProps> = ({ dataAgentId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedCustomerId: customerId, selectedClientId: clientId } = useSelector(
    (state: RootState) => state.vaultilake,
  );

  const [isAddSourceOpen, setIsAddSourceOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<any>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // State for confirmation dialog
  const [dialog, setDialog] = useState<{
    open: boolean;
    group: any | null;
    newStatus: boolean | null;
  }>({
    open: false,
    group: null,
    newStatus: null,
  });

  const { data, isLoading, isError, error } = useGetDataAgentSourcesQuery({
    data_agent_id: dataAgentId,
    include_lakehouses_and_data_connector: true,
  });

  // New query without include_lakehouses_and_data_connector for disable/enable functionality
  const { data: disableEnableSourceList } = useGetDataAgentSourcesQuery({
    data_agent_id: dataAgentId,
  });

  const [enableDisableDataAgentSources] = useEnableDisableDataAgentSourcesMutation();

  const dataAgentSources = useMemo(() => {
    if (isError && (error as any)?.status === 404) {
      return [];
    }
    return data?.data_agent_sources ?? [];
  }, [data, isError, error]);

  // Create unique lakehouse and data connector ID arrays
  const { lakehouseIdArray, dataConnectorIdArray } = useMemo(() => {
    const lakehouseIds: string[] = [];
    const dataConnectorIds: string[] = [];

    dataAgentSources.forEach((source: any) => {
      if (source.SourceType === 'Lakehouse' && source.SourceId) {
        lakehouseIds.push(source.SourceId);
      } else if (
        (source.SourceType === 'DataConnection' || source.SourceType === 'Data Connector') &&
        source.SourceId
      ) {
        dataConnectorIds.push(source.SourceId);
      }
    });

    // Remove duplicates using Set
    return {
      lakehouseIdArray: [...new Set(lakehouseIds)],
      dataConnectorIdArray: [...new Set(dataConnectorIds)],
    };
  }, [dataAgentSources]);

  // Group sources by unique IDs to display names
  const { lakehouseSourcesGrouped, dataConnectorSourcesGrouped } = useMemo(() => {
    const lakehouseMap = new Map<string, any>();
    const dataConnectorMap = new Map<string, any>();

    dataAgentSources.forEach((source: any) => {
      if (source.SourceType === 'Lakehouse' && source.SourceId) {
        if (!lakehouseMap.has(source.SourceId)) {
          lakehouseMap.set(source.SourceId, {
            sourceId: source.SourceId,
            name: source.Lakehouse?.Name || t('dataAgentSources.unnamedLakehouse'),
            code: source.Lakehouse?.Code || '',
            description: source.Lakehouse?.Description || '',
            isActive: source.IsActive ?? true,
            sources: [],
          });
        }
        lakehouseMap.get(source.SourceId)?.sources.push(source);
      } else if (
        (source.SourceType === 'DataConnection' || source.SourceType === 'Data Connector') &&
        source.SourceId
      ) {
        if (!dataConnectorMap.has(source.SourceId)) {
          dataConnectorMap.set(source.SourceId, {
            sourceId: source.SourceId,
            name: source.DataConnector?.Name || t('dataAgentSources.unnamedDataConnection'),
            description: source.DataConnector?.Description || '',
            isActive: source.IsActive ?? true,
            sources: [],
          });
        }
        dataConnectorMap.get(source.SourceId)?.sources.push(source);
      }
    });

    return {
      lakehouseSourcesGrouped: Array.from(lakehouseMap.values()),
      dataConnectorSourcesGrouped: Array.from(dataConnectorMap.values()),
    };
  }, [dataAgentSources, t]);

  const existingSourceType = useMemo(() => {
    if (lakehouseSourcesGrouped.length > 0) return 'Lakehouse';
    if (dataConnectorSourcesGrouped.length > 0) return 'DataConnection';
    return null;
  }, [lakehouseSourcesGrouped, dataConnectorSourcesGrouped]);

  const canAddSource = existingSourceType === null;
  const hasRealError = isError && (error as any)?.status !== 404;

  const handleAddSource = () => {
    setEditingSource(null);
    setIsAddSourceOpen(true);
  };

  const handleCloseAddSource = () => {
    setIsAddSourceOpen(false);
    setEditingSource(null);
    setIsFullscreen(false); // Reset fullscreen when closing
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleSettings = (source: any) => {
    const mappedSource = {
      ...source,
      source_id: source.SourceId,
      DataConnectionId: source.SourceId,
      LakehouseId: source.SourceId,
      DataAgentSourceId: source.DataAgentSourceId,
    };

    setEditingSource(mappedSource);
    setIsAddSourceOpen(true);
  };

  // Handle status toggle initiation
  const handleToggleStatus = (group: any, sourceType: 'Lakehouse' | 'Data Connector') => {
    const newStatus = !group.isActive;
    setDialog({
      open: true,
      group: { ...group, sourceType },
      newStatus,
    });
  };

  // Handle status update after confirmation
  const handleConfirmStatusUpdate = async () => {
    if (!dialog.group || dialog.newStatus === null) return;

    try {
      // Determine if this is a Lakehouse group
      const isLakehouseGroup = dialog.group.sourceType === 'Lakehouse';

      // Get all DataAgentSourceIds for this group
      let sourceIdsToUpdate: string[] = [];

      if (isLakehouseGroup && disableEnableSourceList?.data_agent_sources) {
        // Filter all sources that belong to this lakehouse SourceId
        sourceIdsToUpdate = disableEnableSourceList.data_agent_sources
          .filter((source: any) => source.SourceType === 'Lakehouse' && source.SourceId === dialog.group.sourceId)
          .map((source: any) => source.DataAgentSourceId);
      } else if (disableEnableSourceList?.data_agent_sources) {
        // For Data Connectors, filter by SourceType (support both formats)
        sourceIdsToUpdate = disableEnableSourceList.data_agent_sources
          .filter(
            (source: any) =>
              (source.SourceType === 'DataConnection' || source.SourceType === 'Data Connector') &&
              source.SourceId === dialog.group.sourceId,
          )
          .map((source: any) => source.DataAgentSourceId);
      }

      const enableDisableSource = dialog.newStatus;

      // Create the payload with customerId and clientId
      const updatePayload = {
        data_agent_id: dataAgentId,
        customer_id: customerId || '',
        client_id: clientId || '',
        disable_enable_source_list: sourceIdsToUpdate || [],
        enable_disable_source: enableDisableSource,
      };
      console.log('Update Payload:', updatePayload);
      // Call the new mutation
      await enableDisableDataAgentSources(updatePayload).unwrap();

      dispatch(
        showToaster({
          message: enableDisableSource
            ? t('dataAgentSources.messages.activated', { name: dialog.group.name })
            : t('dataAgentSources.messages.deactivated', { name: dialog.group.name }),
          type: 'success',
        }),
      );
    } catch (err: any) {
      dispatch(
        showToaster({
          message: err.data?.message || t('dataAgentSources.errors.updateStatusFailed'),
          type: 'error',
        }),
      );
    } finally {
      setDialog({ open: false, group: null, newStatus: null });
    }
  };

  // Handle dialog cancel/close
  const handleDialogClose = () => {
    setDialog({ open: false, group: null, newStatus: null });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (hasRealError) {
    return (
      <Box p={2}>
        <Typography color="error">
          {t('dataAgentSources.errors.loadingFailed')}:{' '}
          {(error as any)?.data?.message || t('dataAgentSources.errors.unknownError')}
        </Typography>
      </Box>
    );
  }

  // Empty State Component - Removed Add Source button
  const EmptyState = ({ type }: { type: 'lakehouse' | 'dataConnector' }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
        px: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
      }}
    >
      <StorageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body2" color="text.secondary" align="center">
        {type === 'lakehouse'
          ? t('dataAgents.dataAgentSources.noLakehouseSources')
          : t('dataAgents.dataAgentSources.noDataConnectorSources')}
      </Typography>
    </Box>
  );

  return (
    <>
      <Box>
        {/* Header Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            {/* {t('dataAgents.dataAgentSources.title')} */}
            Configuration
          </Typography>
          {canAddSource && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddSource}
              sx={{ textTransform: 'none' }}
            >
              {t('dataAgents.dataAgentSources.addSource')}
            </Button>
          )}
        </Box>

        {/* Lakehouse Sources - Grouped by unique SourceId */}
        {existingSourceType === 'Lakehouse' && (
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
              {t('dataAgents.dataAgentSources.lakehouses')}
            </Typography>
            {lakehouseSourcesGrouped.length > 0 ? (
              lakehouseSourcesGrouped.map((group: any) => (
                <Box
                  key={group.sourceId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    mb: 1,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <StorageIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {group.name}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        {t('dataAgents.dataAgentSources.sourcesConfigured', { count: group.sources.length })}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <IconButton size="small" onClick={() => handleSettings(group.sources[0])} sx={{ color: '#666' }}>
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <EmptyState type="lakehouse" />
            )}
          </Box>
        )}

        {/* Data Connector Sources - Grouped by unique SourceId */}
        {existingSourceType === 'DataConnection' && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
              {t('dataAgents.dataAgentSources.dataConnectors')}
            </Typography>
            {dataConnectorSourcesGrouped.length > 0 ? (
              dataConnectorSourcesGrouped.map((group: any) => (
                <Box
                  key={group.sourceId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    mb: 1,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <StorageIcon sx={{ color: 'success.main', fontSize: 24 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {group.name}
                      </Typography>
                      {group.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {group.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {t('dataAgents.dataAgentSources.sourcesConfigured', { count: group.sources.length })}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <IconButton size="small" onClick={() => handleSettings(group.sources[0])} sx={{ color: '#666' }}>
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                    <Tooltip
                      title={
                        group.isActive
                          ? t('dataAgents.dataAgentSources.disableSource')
                          : t('dataAgents.dataAgentSources.enableSource')
                      }
                    >
                      <Switch
                        checked={group.isActive}
                        onChange={() => handleToggleStatus(group, 'Data Connector')}
                        color="primary"
                        size="small"
                      />
                    </Tooltip>
                  </Box>
                </Box>
              ))
            ) : (
              <EmptyState type="dataConnector" />
            )}
          </Box>
        )}
      </Box>

      {/* Add/Edit Source Dialog with Fullscreen Support */}
      <Dialog
        open={isAddSourceOpen}
        onClose={handleCloseAddSource}
        fullScreen={isFullscreen}
        PaperProps={{
          sx: {
            width: isFullscreen ? '100vw' : '80vw',
            maxWidth: isFullscreen ? '100vw' : '900px',
            height: isFullscreen ? '100vh' : '90vh',
            maxHeight: isFullscreen ? '100vh' : '90vh',
            display: 'flex',
            flexDirection: 'column',
            m: isFullscreen ? 0 : 2,
          },
        }}
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">
            {editingSource
              ? t('dataAgents.dataAgentSources.editSourceTitle')
              : t('dataAgents.dataAgentSources.addSourceTitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={isFullscreen ? t('common.exitFullscreen') : t('common.enterFullscreen')}>
              <IconButton
                edge="end"
                color="inherit"
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                sx={{ color: 'text.secondary' }}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            <IconButton edge="end" color="inherit" onClick={handleCloseAddSource} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, flex: 1, overflow: 'auto' }}>
          <AddEditDataAgentSourceUIForm
            onCancel={handleCloseAddSource}
            initialData={editingSource}
            dataAgentId={dataAgentId}
            existingSourceType={existingSourceType}
          />
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <CustomConfirmDialog
        open={dialog.open}
        title={t('dataAgents.dataAgentSources.confirmDialog.title')}
        message={
          dialog.newStatus === true
            ? t('dataAgents.dataAgentSources.confirmDialog.activateMessage', { name: dialog.group?.name })
            : t('dataAgents.dataAgentSources.confirmDialog.deactivateMessage', { name: dialog.group?.name })
        }
        onConfirm={handleConfirmStatusUpdate}
        onCancel={handleDialogClose}
        onClose={handleDialogClose}
        confirmLabel={t('dataAgents.dataAgentSources.confirmDialog.confirm')}
        cancelLabel={t('dataAgents.dataAgentSources.confirmDialog.cancel')}
      />
    </>
  );
};

export default DataAgentSourceTab;
